import { AsyncPanel } from '../ui/AsyncPanel'
import type { ResourceState } from '../data/resource'
import type { CreditEntry, OrderView } from '../data/endpoints'
import { CustomerShell, rupees } from './CustomerShell'

/**
 * Orders, addresses and the credit ledger on one screen.
 *
 * Three tabs would be tidier and worse: this is a customer who opens the app once
 * a month to ask "where is my parcel and how much credit do I have", and both
 * answers should be on the screen they land on. Addresses sit below because they
 * are edited rarely and read never.
 *
 * The credit figure here is the replayed balance from `GET
 * /customers/:id/credit-balance`, and the entries below it are the ledger that
 * produced it. Showing both is what lets a customer who disagrees with the number
 * point at the line they disagree with, instead of messaging Mitali to ask.
 */
export interface AddressView {
  id: string
  label: string
  line1: string
  line2?: string | null
  city: string
  pincode: string
  isDefault: boolean
}

export interface AccountProps {
  creditPaise: number | null
  orders: ResourceState<{ items: OrderView[] }>
  credit: ResourceState<{ items: CreditEntry[] }>
  addresses: ResourceState<{ items: AddressView[] }>
  onOpenOrder?: (orderId: string) => void
  onAddAddress?: () => void
  onRetryOrders?: () => void
  onRetryCredit?: () => void
  onRetryAddresses?: () => void
  online?: boolean
}

export function Account({
  creditPaise,
  orders,
  credit,
  addresses,
  onOpenOrder,
  onAddAddress,
  onRetryOrders,
  onRetryCredit,
  onRetryAddresses,
  online,
}: AccountProps) {
  return (
    <CustomerShell title="Your account" creditPaise={creditPaise} cart={null} online={online}>
      <section className="sf-section">
        <h2 className="t-title">Orders</h2>
        <AsyncPanel state={orders} emptyMessage="No orders yet" onRetry={onRetryOrders}>
          {(o) => (
            <ul className="sf-lines">
              {o.items.map((order) => (
                <li key={order.id} className="sf-line">
                  <button type="button" className="sf-line-hit" onClick={() => onOpenOrder?.(order.id)}>
                    <span className="sf-line-name">{order.orderNo}</span>
                    <span className={`chip chip-status chip-${order.status}`}>{order.status}</span>
                    <span className="sf-line-total">{rupees(order.totalPaise)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </AsyncPanel>
      </section>

      <section className="sf-section">
        <h2 className="t-title">Credit</h2>
        <p className="sf-credit-balance t-display">{creditPaise === null ? '-' : rupees(creditPaise)}</p>
        <AsyncPanel state={credit} emptyMessage="No credit movements yet" onRetry={onRetryCredit}>
          {(c) => (
            <ul className="sf-lines">
              {c.items.map((entry, i) => (
                <li key={`${entry.createdAt ?? i}-${i}`} className="sf-line">
                  <span className="sf-line-name">{entry.reason ?? entry.direction}</span>
                  <span className={`sf-line-total sf-${entry.direction}`}>
                    {entry.direction === 'debit' ? '-' : '+'}
                    {rupees(entry.amountPaise)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </AsyncPanel>
      </section>

      <section className="sf-section">
        <h2 className="t-title">Addresses</h2>
        <AsyncPanel state={addresses} emptyMessage="No address saved - add one before you check out" onRetry={onRetryAddresses}>
          {(a) => (
            <ul className="sf-lines">
              {a.items.map((addr) => (
                <li key={addr.id} className="sf-line">
                  <span className="sf-line-name">
                    {addr.label}
                    {addr.isDefault ? <span className="chip chip-default">Default</span> : null}
                  </span>
                  <span className="sf-address">
                    {[addr.line1, addr.line2, addr.city, addr.pincode].filter(Boolean).join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </AsyncPanel>
        <button type="button" className="btn thumb-primary" onClick={onAddAddress}>
          Add an address
        </button>
      </section>
    </CustomerShell>
  )
}
